import { useLayoutEffect, useRef, useState } from 'react';
import { FunnelIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Button } from './Button';
import { Card } from './Card';
import { Input } from './Input';

interface CompactFilterBarProps {
  /** Omit both `search` and `onSearchChange` for a page with no free-text
   * search concept (e.g. Reports, which filters an aggregate, not a row
   * list) -- the search box is left out entirely rather than rendered with
   * nothing wired to it. */
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  activeFilterCount: number;
  onClearFilters?: () => void;
  /**
   * How many filter controls to fit per row at the widest breakpoint.
   * Defaults to 4, which is what every caller had before this existed.
   *
   * Queue #213 asked for the filters on a single row where the width allows.
   * A page with six controls needs six columns to manage that; forcing every
   * page to six would leave a three-filter page with a row of stranded gaps,
   * so the density is the caller's to state.
   */
  columns?: 4 | 5 | 6;
  /**
   * Controlled expansion. Omit it (the default) and the bar owns the state:
   * it starts expanded when `activeFilterCount > 0`, opens again on any later
   * 0 -> n transition, and otherwise only moves when the user clicks Filter.
   *
   * Pass it and the caller owns the state outright -- the bar renders exactly
   * what it is given and never opens or closes itself, it only reports the
   * user's intent through `onExpandedChange`.
   *
   * CALLER'S DUTY: passing `expanded={false}` while `activeFilterCount > 0` is
   * permitted ONLY as the result of the user's own click on Filter -- a
   * controlled caller must never render that combination on its own, because it
   * hides every control that explains why the list is filtered, which is the
   * failure this component exists to prevent. (The count badge and Clear button
   * stay visible in that state, so an active filter is still announced, but the
   * values behind it are not.)
   *
   * A controlled bar also never banks expansion state of its own: if the caller
   * later stops passing `expanded`, the panel keeps showing the user's last
   * explicit intent rather than an auto-open the caller had overruled.
   */
  expanded?: boolean;
  /**
   * Called with the requested next state whenever the user clicks Filter.
   * Useful uncontrolled too (e.g. to persist the preference); the bar still
   * updates itself in that case.
   */
  onExpandedChange?: (next: boolean) => void;
  /** The filter controls, revealed on expand. */
  children: React.ReactNode;
}

// Tailwind resolves class names statically, so the breakpoint variants have to
// appear as whole literals -- a template string like `xl:grid-cols-${n}` is not
// in the stylesheet at build time and silently renders unstyled.
const COLUMN_CLASSES: Record<4 | 5 | 6, string> = {
  4: 'sm:grid-cols-2 lg:grid-cols-4',
  5: 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5',
  6: 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6',
};

/**
 * A one-line search + Filter-toggle strip that collapses the actual filter
 * controls into a bordered panel below, shown only on expand. Used by
 * Contacts (Broker 2) and Policies (Broker 3) to replace a filter block that
 * previously occupied two or three lines with every control always visible.
 *
 * The panel opens whenever `activeFilterCount` goes from 0 to nonzero -- on
 * mount, or on any later render. A filter that is set but whose control sits
 * inside a collapsed panel would otherwise be invisible to the user, which is
 * a worse failure than the multi-line layout this replaces (see Task 1's
 * field-bound-errors lesson — hiding active state is not an acceptable side
 * effect of a display change). The first render is settled by the useState
 * initializer; a count a caller computes asynchronously is caught before paint
 * by a layout effect, so the panel is never painted collapsed over a filtered
 * list.
 *
 * It deliberately does NOT mirror `activeFilterCount`. The panel never closes
 * itself, and it never re-opens while the count merely stays nonzero -- a user
 * who collapses it with filters applied keeps it collapsed, because a panel
 * that reopened on every render would be unusable. The badge and Clear button
 * stay visible in that state, so the active filters are still announced.
 */
export function CompactFilterBar({
  search,
  onSearchChange,
  searchPlaceholder = 'Search...',
  activeFilterCount,
  onClearFilters,
  columns = 4,
  expanded: expandedProp,
  onExpandedChange,
  children,
}: CompactFilterBarProps) {
  const isControlled = expandedProp !== undefined;
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(activeFilterCount > 0);
  const expanded = isControlled ? expandedProp : uncontrolledExpanded;

  // The previous count, so the effect below can tell a 0 -> n TRANSITION from
  // a count that is simply still nonzero. Seeding it with the mount value is
  // what makes the mount case a no-op here: the initial useState above has
  // already handled it, so a caller that renders with filters active on the
  // first paint behaves byte-for-byte as it did before this prop existed.
  const previousActiveFilterCount = useRef(activeFilterCount);

  // useLayoutEffect, NOT useEffect. The batch design's rule is that a restored
  // filter must never render behind a collapsed panel; a passive effect flushes
  // AFTER paint, so a caller whose count resolves on a later render would paint
  // one full frame collapsed and then pop open -- a visible jump, and for that
  // frame exactly the "filtered list with nothing on screen to explain it" this
  // component exists to prevent. A layout effect runs before the browser
  // paints, so no collapsed frame is ever shown. The first-render case does not
  // rely on this at all: it is settled by the useState initializer above (and
  // useFilterParams restores from the URL in its own lazy initializer, so the
  // main case already has a nonzero count on render one). This is the safety
  // net for the asynchronous case only.
  //
  // Skipped entirely while controlled: a controlled bar must never bank hidden
  // expansion state behind the caller's back, so that if the caller later stops
  // passing `expanded`, the panel reflects the user's last explicit intent
  // rather than springing open from an auto-open the caller had overruled.
  useLayoutEffect(() => {
    const previous = previousActiveFilterCount.current;
    previousActiveFilterCount.current = activeFilterCount;
    if (isControlled) return;
    if (previous === 0 && activeFilterCount > 0) {
      // Only ever opens. Nothing in this component closes the panel except the
      // user's own click on Filter.
      setUncontrolledExpanded(true);
    }
  }, [activeFilterCount, isControlled]);

  const handleToggle = () => {
    const next = !expanded;
    if (!isControlled) setUncontrolledExpanded(next);
    onExpandedChange?.(next);
  };

  const hasSearch = onSearchChange !== undefined;

  return (
    <Card className="mb-6">
      <div className="flex items-center gap-3">
        {hasSearch && (
          <div className="flex-1">
            <Input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
            />
          </div>
        )}
        <Button
          type="button"
          variant="outline"
          aria-expanded={expanded}
          onClick={handleToggle}
          leftIcon={<FunnelIcon className="w-4 h-4" />}
        >
          Filter
          {activeFilterCount > 0 && (
            <>
              {' '}
              <span className="ml-2 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary-600 px-1.5 py-0.5 text-xs font-semibold text-white">
                {activeFilterCount}
              </span>
            </>
          )}
        </Button>
        {activeFilterCount > 0 && onClearFilters && (
          <Button type="button" variant="ghost" onClick={onClearFilters}>
            Clear
          </Button>
        )}
      </div>
      {expanded && (
        <div className="mt-4 border-t border-gray-100 dark:border-gray-700 pt-4">
          <div className={`grid grid-cols-1 gap-4 ${COLUMN_CLASSES[columns]}`}>{children}</div>
        </div>
      )}
    </Card>
  );
}
